<div class="invoice-preview" [class.dialog-mode]="isDialogMode">
  <div class="preview-shell">
    <div class="preview-header">
      <div class="header-copy">
        <p class="eyebrow">Purchase Bill</p>
        <div class="header-title-row">
          <h2>Accounts Payable Print Preview</h2>
          <div class="toolbar" aria-label="Purchase bill actions">
            <mat-slide-toggle
              class="sign-toggle"
              color="primary"
              [checked]="includeSignature"
              (change)="onSignatureToggle($event.checked)">
              Sign + Stamp
            </mat-slide-toggle>
            <button mat-raised-button color="primary" (click)="printPdf()">
              Print
            </button>
            <button mat-stroked-button color="primary" (click)="downloadPdf()">
              Download PDF
            </button>
            <button mat-flat-button color="accent" (click)="sendMail()">
              Mail Invoice
            </button>

            <button
              *ngIf="isDialogMode"
              mat-icon-button
              type="button"
              class="close-preview-button"
              aria-label="Close preview"
              (click)="closePreview()">
              <mat-icon svgIcon="mat:close"></mat-icon>
            </button>
          </div>
        </div>
        <p class="subtext">
          Purchase-bill focused preview with vendor summary, GRN traceability,
          tax totals, and optional sign or stamp.
        </p>
      </div>
    </div>

    <div class="preview-frame" *ngIf="previewData; else loading">
      <div class="invoice-page">
        <div class="invoice-top-strip">
          <span></span>
          <span></span>
        </div>

        <div class="invoice-head">
          <div class="logo-cell">
            <img
              *ngIf="previewData.logo; else logoFallback"
              [src]="previewData.logo"
              alt="Company Logo" />
            <ng-template #logoFallback>
              <div class="logo-fallback">{{ previewData.companyInitials }}</div>
            </ng-template>
          </div>

          <div class="company-cell">
            <div class="document-kicker">Procurement Department</div>
            <h1>{{ previewData.companyName | uppercase }}</h1>
            <p *ngIf="previewData.companyAddress">
              {{ previewData.companyAddress }}
            </p>
            <p *ngFor="let meta of previewData.companyMeta">{{ meta }}</p>
          </div>

          <div class="invoice-meta-box">
            <div class="meta-badge">PURCHASE BILL</div>
            <p><strong>Bill No:</strong> {{ previewData.billNo }}</p>
            <p><strong>Bill Date:</strong> {{ previewData.billDate }}</p>
            <p><strong>Party Inv No:</strong> {{ previewData.partyInvNo || "-" }}</p>
            <p><strong>Party Inv Date:</strong> {{ previewData.partyInvDate || "-" }}</p>
          </div>
        </div>

        <div class="po-overview">
          <div class="overview-card" *ngFor="let card of previewData.overview">
            <span class="overview-label">{{ card.label }}</span>
            <strong>{{ card.value }}</strong>
          </div>
        </div>

        <div class="party-grid two-column-grid">
          <section>
            <h3>Vendor Information</h3>
            <h4>{{ previewData.vendorName }}</h4>
            <p *ngIf="previewData.vendorAddress">
              {{ previewData.vendorAddress }}
            </p>
            <p *ngFor="let line of previewData.vendorMeta">{{ line }}</p>
          </section>

          <section>
            <h3>Warehouse & Bill Controls</h3>
            <h4>{{ previewData.warehouseName }}</h4>
            <p *ngIf="previewData.warehouseAddress">
              {{ previewData.warehouseAddress }}
            </p>
            <p *ngFor="let flag of previewData.statusFlags">
              {{ flag.label }}: {{ flag.value }}
            </p>
          </section>
        </div>

        <div class="linked-docs" *ngIf="previewData.grnCards.length">
          <h3>Linked PO / GRN References</h3>
          <div class="linked-doc-grid">
            <div class="linked-doc-card" *ngFor="let card of previewData.grnCards">
              <strong>{{ card.poCode || "-" }} / {{ card.grnCode || "-" }}</strong>
              <p>PO Date: {{ card.poDate || "-" }}</p>
              <p>GRN Date: {{ card.grnDate || "-" }}</p>
            </div>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th>SR</th>
              <th>DESCRIPTION</th>
              <th>HSN / SAC</th>
              <th>UOM</th>
              <th>QTY</th>
              <th>RATE</th>
              <th>DIS %</th>
              <th>TAXABLE</th>
              <th>TAX</th>
              <th>LINE TOTAL</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of previewData.items">
              <td class="center">{{ item.sr }}</td>
              <td>
                <div>{{ item.productName }}</div>
                <div class="item-remark" *ngIf="item.remarks && item.remarks !== '-'">
                  [{{ item.remarks }}]
                </div>
                <div class="item-remark secondary" *ngIf="item.poCode || item.grnCode">
                  <span *ngIf="item.poCode">PO: {{ item.poCode }}</span>
                  <span *ngIf="item.grnCode">GRN: {{ item.grnCode }}</span>
                </div>
              </td>
              <td class="center">{{ item.hsn || "-" }}</td>
              <td class="center">{{ item.uom }}</td>
              <td class="right">{{ item.qty }}</td>
              <td class="right">{{ item.rate }}</td>
              <td class="right">{{ item.discount }}</td>
              <td class="right">{{ item.taxable }}</td>
              <td class="right">{{ item.tax }}</td>
              <td class="right strong">{{ item.lineTotal }}</td>
            </tr>
            <tr class="grand-row">
              <td colspan="9" class="right">GRAND TOTAL</td>
              <td class="right">{{ previewData.netAmount }}</td>
            </tr>
          </tbody>
        </table>

        <div class="bottom-grid">
          <div class="details-block">
            <section class="words-block">
              <h3>Amount In Words</h3>
              <p>{{ previewData.amountInWords }}</p>
            </section>
            <section *ngIf="previewData.taxBreakup.length">
              <h3>Tax Breakup</h3>
              <table class="tax-breakup-table">
                <thead>
                  <tr>
                    <th>TAX HEAD</th>
                    <th>RATE %</th>
                    <th>TAXABLE</th>
                    <th>TAX AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let tax of previewData.taxBreakup">
                    <td>{{ tax.name }}</td>
                    <td class="right">{{ tax.rate }}</td>
                    <td class="right">{{ tax.taxableAmount }}</td>
                    <td class="right strong">{{ tax.taxAmount }}</td>
                  </tr>
                </tbody>
              </table>
            </section>
            <section *ngIf="previewData.remarks !== '-'">
              <h3>Remarks</h3>
              <p>{{ previewData.remarks }}</p>
            </section>
          </div>

          <div class="summary-sign-block">
            <table class="summary-table">
              <tr>
                <td>Taxable Amount</td>
                <td>{{ previewData.taxableAmount }}</td>
              </tr>
              <tr>
                <td>Tax Amount</td>
                <td>{{ previewData.taxAmount }}</td>
              </tr>
              <tr>
                <td>TDS Amount</td>
                <td>{{ previewData.tdsAmount }}</td>
              </tr>
              <tr>
                <td>Invoice Amount</td>
                <td>{{ previewData.invoiceAmount }}</td>
              </tr>
              <tr>
                <td>Round Off</td>
                <td>{{ previewData.roundOffAmount }}</td>
              </tr>
              <tr>
                <td>Net Amount</td>
                <td>{{ previewData.netAmount }}</td>
              </tr>
            </table>

            <div class="html-sign-area">
              <div class="sign-company">
                For {{ previewData.companyNameTitle }}
              </div>
              <div class="sign-assets" *ngIf="includeSignature">
                <img
                  *ngIf="previewData.stampImage"
                  class="stamp-img"
                  [src]="previewData.stampImage"
                  alt="Company Stamp" />
                <img
                  *ngIf="previewData.signatureImage"
                  class="signature-img"
                  [src]="previewData.signatureImage"
                  alt="Signature" />
              </div>
              <div class="sign-name" *ngIf="previewData.signatoryName">
                {{ previewData.signatoryName }}
              </div>
              <div class="sign-role">
                {{ previewData.signatoryTitle || "Authorized Signatory" }}
              </div>
            </div>
          </div>
        </div>

        <div class="invoice-footer">
          <div class="footer-line">
            <span></span>
            <span></span>
          </div>
          <div class="footer-text">
            <span>Generated using VCS ERP</span>
            <span>Page 1 of 1</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template #loading>
    <div class="loading-state">
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      <p class="loading-text">
        {{
          includeSignature
            ? "Generating signed purchase bill preview..."
            : "Generating purchase bill preview..."
        }}
      </p>
    </div>
  </ng-template>
</div>
